import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { capability, FESTIVAL_STACK_MONTHLY } from '@gjl/voice/pricing'

// Prices render from @gjl/voice/pricing. Edit them there.
const applications = capability('applications')!

const INLINE_LINK =
  'text-primary underline decoration-1 underline-offset-4 hover:decoration-2 focus-visible:outline-primary focus-visible:outline-2 focus-visible:outline-offset-4'

const PROBLEMS = [
  {
    problem: 'You pay for every applicant you attract',
    description:
      'Per-applicant pricing charges you most in the year recruiting finally works. A good season becomes a bigger invoice, which is exactly backwards.',
  },
  {
    problem: 'Applicants leave your site to apply',
    description:
      'The form lives on a vendor domain with a vendor logo. The moment a prospective student is most engaged, you hand them to someone else’s brand.',
  },
  {
    problem: 'General forms cannot describe music',
    description:
      'Repertoire with composer and movement, per-excerpt video, voice part and instrument, a teacher recommendation that arrives separately: none of it fits a generic form builder.',
  },
  {
    problem: 'Review happens in a spreadsheet anyway',
    description:
      'Faculty download files, score in a shared sheet, and email the results around. The platform stored the applications and stopped there.',
  },
]

const CAPABILITIES = [
  'Repertoire fields with composer, work, and movement',
  'Per-piece and per-movement audition video upload',
  'Teacher and conductor recommendations, requested and tracked separately',
  'Instrument, voice part, and program routing',
  'Application fees and fee waivers, collected on your own site',
  'Faculty review workflow with scoring and notes',
  'Accept, waitlist, and decline decisions with the emails attached',
  'Prior-season applicants stay searchable alongside the new cycle',
]

const FAQS = [
  {
    q: 'What does application and audition management cost?',
    a: `$${applications.priceMonthly} a month, flat. There is no per-applicant fee, so a strong recruiting year costs the same as a quiet one. Festivals usually run applications alongside the website, email, ticketing, and donor management, which comes to $${FESTIVAL_STACK_MONTHLY}/mo all in.`,
  },
  {
    q: 'Can you handle audition video?',
    a: 'Yes. Applicants upload video per piece or per movement, and reviewers watch inside the review workflow rather than downloading files. Reliable large-file upload was a day-one requirement, not an add-on.',
  },
  {
    q: 'We already use Acceptd. Can we move mid-cycle?',
    a: 'Moving between cycles is cleaner, and a typical rebuild takes weeks rather than months. Export your historical applicants and we import them so prior seasons stay searchable. Bring your open date to the call and you get a straight answer about whether it is realistic.',
  },
  {
    q: 'Do applicants create yet another account?',
    a: 'They create one account on your site, for your program. There is no marketplace profile to maintain and no other program advertised to them while they apply.',
  },
  {
    q: 'Who reviews, and what do they see?',
    a: 'Faculty sign in and see the applications assigned to them, with materials, video, and a scoring form in one place. You control who reviews which pool.',
  },
]

export default function ApplicationsPage() {
  const breadcrumbs = generateBreadcrumbs('/services/applications', breadcrumbLabels)

  useSEO({
    title: 'Application and Audition Software for Music Festivals',
    // Held to 120-160 chars: Google truncates past ~160, and the tail was where
    // the differentiators lived. Price interpolates from pricing.ts — never hard-coded.
    description: `Application and audition management built for music: repertoire fields, per-movement audition video, and faculty review. $${applications.priceMonthly}/mo, no per-applicant fees.`,
    url: '/services/applications',
    canonical: '/services/applications',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Application and Audition Software for Music Festivals',
      description:
        'Application and audition management built for music: repertoire fields, per-movement audition video, recommendations, and faculty review workflow, running on your own website with no per-applicant fees.',
      url: '/services/applications',
    }),
  })

  // The hero keeps its entrance; everything below the fold is static. The
  // per-section staggered reveals and card hover lifts were the stock kit.
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)

  return (
    <div className="mx-auto max-w-6xl space-y-28 px-5 py-16 md:space-y-36 md:px-12">
      {/* Hero */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Application and audition software built for music</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Repertoire, per-movement audition video, recommendations, and faculty review, running on your own website
          instead of a vendor’s marketplace.
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          ${applications.priceMonthly} a month, flat. No per-applicant fees, so the year your recruiting works is not
          the year your bill goes up.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <m.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </m.div>
          </a>
          <Link to="/compare/acceptd">
            <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
              Compare vs Acceptd
            </Button>
          </Link>
        </div>
      </section>

      {/* Problems: a typographic list, name left and explanation right, in
          place of four bordered cards. Space alone separates the rows. */}
      <section aria-labelledby="problems" className="space-y-12">
        <h2 id="problems" className="text-fluid-h3 max-w-3xl">
          Why not a general admissions platform?
        </h2>
        <ul className="space-y-10 md:space-y-12">
          {PROBLEMS.map((item) => (
            <li key={item.problem} className="grid gap-3 md:grid-cols-12 md:items-baseline md:gap-8">
              <h3 className="text-fluid-h4 md:col-span-5">{item.problem}</h3>
              <p className="text-muted-foreground max-w-[60ch] leading-relaxed md:col-span-7">{item.description}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* Built by someone who has been on all three sides of an audition */}
      <section aria-labelledby="three-sides" className="grid gap-6 md:grid-cols-12 md:gap-8">
        <h2 id="three-sides" className="text-fluid-h3 md:col-span-5">
          Built by someone who has been on all three sides of an audition
        </h2>
        <div className="text-muted-foreground space-y-4 leading-relaxed md:col-span-7">
          <p className="text-fluid-body max-w-[60ch]">
            The field list here is not a guess about what music programs need. It comes from applying to programs and
            paying the fee, from sitting on the panel that picks section leaders and choral scholars, and from building
            the systems that run the cycle. Every side except the one that bills you.
          </p>
          <p className="max-w-[60ch]">
            Read the{' '}
            <Link to="/blog/festivals-losing-applicants" className={INLINE_LINK}>
              applicant drop-off breakdown
            </Link>{' '}
            or see{' '}
            <Link to="/who-we-help/music-festivals" className={INLINE_LINK}>
              what we build for festivals
            </Link>
            .
          </p>
        </div>
      </section>

      {/* What you get. A plain two-column list; the check icons on every line
          carried no information, since every line is included. */}
      <section aria-labelledby="whats-in-it" className="space-y-12">
        <h2 id="whats-in-it" className="text-fluid-h3">
          What is in it
        </h2>
        <ul className="grid gap-x-10 gap-y-6 md:grid-cols-2">
          {CAPABILITIES.map((c) => (
            <li key={c} className="text-fluid-body max-w-[48ch] leading-relaxed">
              {c}
            </li>
          ))}
        </ul>
        <p className="text-muted-foreground max-w-[60ch] leading-relaxed">
          Applications sit on the same audience record as your{' '}
          <Link to="/services/email-marketing" className={INLINE_LINK}>
            email
          </Link>{' '}
          and{' '}
          <Link to="/services/donor-management" className={INLINE_LINK}>
            donor management
          </Link>
          , so an applicant who becomes an alum does not become a second contact.
        </p>
      </section>

      {/* Pricing. One plan, so nothing to compare side by side: set as type,
          not as a lone tinted card. */}
      <section aria-labelledby="pricing" className="grid gap-8 md:grid-cols-12">
        <h2 id="pricing" className="text-fluid-h3 md:col-span-5">
          What it costs
        </h2>
        <div className="space-y-6 md:col-span-7">
          <h3 className="text-fluid-h4">{applications.name}</h3>
          <p className="flex items-baseline gap-2">
            <span className="font-display text-6xl leading-none">${applications.priceMonthly}</span>
            <span className="text-muted-foreground">/month</span>
          </p>
          <p className="text-muted-foreground max-w-[60ch] leading-relaxed">
            No per-applicant fee. No annual contract. Add it to the rest of the stack and a full festival deployment is
            ${FESTIVAL_STACK_MONTHLY}/mo.
          </p>
          <Link to="/get-started" className="inline-block">
            <Button size="lg" className="cursor-pointer px-8">
              Get Started
            </Button>
          </Link>
          <p className="text-muted-foreground max-w-[60ch] text-sm leading-relaxed">
            Product details live on the{' '}
            <Link to="/platform/features/applications" className={INLINE_LINK}>
              Applications feature page
            </Link>
            , and{' '}
            <Link to="/pricing" className={INLINE_LINK}>
              pricing
            </Link>{' '}
            has the full catalog. Pay annually on any plan and get 2 months free.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section aria-labelledby="questions" className="space-y-12">
        <h2 id="questions" className="text-fluid-h3">
          Questions
        </h2>
        <ul className="space-y-10 md:space-y-12">
          {FAQS.map((f) => (
            <li key={f.q} className="grid gap-3 md:grid-cols-12 md:gap-8">
              <h3 className="text-xl leading-snug md:col-span-5">{f.q}</h3>
              <p className="text-muted-foreground max-w-[60ch] leading-relaxed md:col-span-7">{f.a}</p>
            </li>
          ))}
        </ul>
        <a href="/book" target="_blank" rel="noopener noreferrer" className="inline-block">
          <Button size="lg" className="cursor-pointer">
            Book a Free Call →
          </Button>
        </a>
      </section>
    </div>
  )
}
